import {
  PageConfig,
  PageConfigSchemaVersion,
} from "../../config/pageConfig.types";
import { DefaultFormId } from "../../config/resolveView";
import { fullWidth } from "../../blocks/layout/columnSpans";
import { ColumnSpanConfig } from "../../blocks/layout/GridColumn.types";
import { automationDeliveryFields } from "./automationDeliveryFields";
import { automationExecutionTabs } from "./automationExecutionTabs";
import {
  automationScheduleModeOptions,
  automationTypeOptions,
} from "./automationTypeOptions";

const statistics = [
  ["emailssent", "Emails sent"],
  ["openrate", "Open rate"],
  ["clickrate", "Click rate"],
  ["clickstoopenrate", "CTO"],
] as const;

// Four to a row from tablet up, two on a phone.
const statisticSpan: ColumnSpanConfig = {
  desktop: 3,
  tablet: 3,
  mobile: 6,
};

/**
 * Automation as page config. The sidebar holds the details, type, schedule type and
 * statistics, the type and schedule as text once saved; delivery is on the left. The
 * steps of the new-record wizard - Details, Type (with the schedule type when scheduled),
 * Delivery - are ordered by `wizardOrder`, as the sidebar comes after the left column in the layout.
 * Published and Testing automations are read-only.
 */
export const automationPageConfig: PageConfig = {
  schemaVersion: PageConfigSchemaVersion,
  entityName: "dwautomation",
  viewId: DefaultFormId,
  pageType: "record",
  rows: [
    {
      id: "row-main",
      columns: [
        {
          id: "column-primary",
          span: { desktop: 9, tablet: 9, mobile: 12 },
          blocks: [
            {
              kind: "section",
              id: "section-statistics",
              title: "",
              fields: statistics.map(([statistic, label]) => ({
                id: `field-automation_${statistic}`,
                type: "component",
                name: `automation_${statistic}`,
                label: "",
                componentKey: "automationStatistic",
                componentProps: { statistic, label },
                excludeFromSave: true,
                span: statisticSpan,
              })),
            },
            {
              kind: "section",
              id: "section-delivery",
              title: "Delivery",
              wizardStep: true,
              wizardOrder: 3,
              visibleWhen: "automationEditableDelivery",
              fields: automationDeliveryFields,
            },
            {
              kind: "section",
              id: "section-delivery-card",
              title: "",
              bare: true,
              visibleWhen: "automationActiveDelivery",
              fields: [
                {
                  id: "field-automation_delivery",
                  type: "component",
                  name: "automation_delivery",
                  label: "",
                  componentKey: "automationDeliveryCard",
                  excludeFromSave: true,
                  span: fullWidth,
                },
              ],
            },
          ],
        },
        {
          id: "column-sidebar",
          span: { desktop: 3, tablet: 3, mobile: 12 },
          card: true,
          fillHeight: true,
          blocks: [
            {
              kind: "section",
              id: "section-details",
              title: "Details",
              wizardStep: true,
              wizardOrder: 1,
              fields: [
                {
                  id: "field-name",
                  type: "text",
                  name: "name",
                  label: "Name",
                  required: true,
                  isNameField: true,
                  span: fullWidth,
                },
                {
                  id: "field-campaignid",
                  type: "lookup",
                  name: "campaignid",
                  label: "Reporting Campaign",
                  lookupEntity: "campaign",
                  searchable: true,
                  required: true,
                  validatorKey: "automationRequiredLookup",
                  span: fullWidth,
                },
              ],
            },
            {
              kind: "section",
              id: "section-type",
              title: "Automation Type",
              wizardStep: true,
              wizardOrder: 2,
              visibleWhen: "automationNew",
              fields: [
                {
                  id: "field-type",
                  type: "radio",
                  name: "type",
                  label: "",
                  cards: true,
                  inline: true,
                  options: automationTypeOptions,
                  helpText:
                    "Choose the automation pattern you want to configure.",
                  validatorKey: "automationType",
                  span: fullWidth,
                },
                {
                  id: "field-schedulemode",
                  type: "radio",
                  name: "schedulemode",
                  label: "Schedule Type",
                  cards: true,
                  inline: true,
                  options: automationScheduleModeOptions,
                  validatorKey: "automationScheduleMode",
                  visibleWhen: "automationIsScheduled",
                  span: fullWidth,
                },
              ],
            },
            {
              kind: "section",
              id: "section-type-saved",
              title: "Automation Type",
              visibleWhen: "automationSaved",
              fields: [
                {
                  id: "field-automation_type",
                  type: "component",
                  name: "automation_type",
                  label: "",
                  componentKey: "automationTypeSummary",
                  excludeFromSave: true,
                  span: fullWidth,
                },
              ],
            },
            {
              kind: "section",
              id: "section-schedule-saved",
              title: "Schedule Type",
              visibleWhen: "automationSavedScheduled",
              fields: [
                {
                  id: "field-automation_schedule",
                  type: "component",
                  name: "automation_schedule",
                  label: "",
                  componentKey: "automationScheduleSummary",
                  excludeFromSave: true,
                  span: fullWidth,
                },
              ],
            },
          ],
        },
      ],
    },
    {
      id: "row-executions",
      columns: [
        {
          id: "column-executions",
          span: fullWidth,
          card: true,
          blocks: [
            {
              kind: "tables",
              id: "tables-executions",
              title: "Executions",
              tabs: automationExecutionTabs,
            },
          ],
        },
      ],
    },
  ],
};
